import React from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuCalendarDays, LuInfo, LuPencil, LuTrash2, LuUpload } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import { useTranslation } from '../../../i18n/LanguageContext';
import WorkerStatusBadge from './WorkerStatusBadge';
import { assignmentsByCenter, formatAssignedDate, formatLeftDate, roleLabel } from './workerOptions';

// Kayıtta tutulan Çalışan Tipi / Kör Durumu değerleri
const KNOWN_TAGS = ['primer', 'backup', 'blinded', 'unblinded'];
const SECTION_TITLE ='tw-mb-2 tw-px-0.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500';

export const initialsOf = (worker) => `${(worker.name || '').trim().charAt(0)}${(worker.surname || '').trim().charAt(0)}`.toLocaleUpperCase('tr-TR') || '?';

/**
 * Seçili çalışanın özeti: roller, merkez / atama sayıları, merkez merkez
 * atamalar (ayrılınanlar tarihleriyle) ve bilgi notu.
 */
const WorkerDetailCard = ({ worker, summary, showHeader }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`projectManagement.workers.detail.${key}`, vars);
  const centers = assignmentsByCenter(worker);
  const info = worker.centers[0]?.info;
  const facts = [
    { key: 'centers', label: label('activeCenters'), value: summary.activeCenterIds.size },
    { key: 'roles', label: label('roleCount'), value: summary.uniqueRoles.length },
    { key: 'assignments', label: label('totalAssignments'), value: worker.centers.length },
  ];

  return (
    <div className="tw-space-y-5">
      {showHeader && (
        <div className="tw-flex tw-items-center tw-gap-3">
          <span aria-hidden="true" className="tw-grid tw-h-11 tw-w-11 tw-shrink-0 tw-place-items-center tw-rounded-2xl tw-bg-gradient-to-br tw-from-violet-400 tw-to-purple-600 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-violet-500/25">
            {initialsOf(worker)}
          </span>
          <div className="tw-min-w-0 tw-flex-1">
            <p className="tw-truncate tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">{`${worker.name || ''} ${worker.surname || ''}`.trim()}</p>
            <p className="tw-mt-0.5 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-500 tw-tabular-nums dark:tw-text-slate-400">
              <LuCalendarDays aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              {formatAssignedDate(worker.assignedAt, locale) || '—'}
            </p>
          </div>
          <WorkerStatusBadge status={summary.status} />
        </div>
      )}

      <dl className="tw-grid tw-grid-cols-3 tw-gap-2">
        {facts.map((fact) => (
          <div key={fact.key} className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
            <dd className="tw-text-lg tw-font-semibold tw-leading-none tw-text-slate-900 tw-tabular-nums dark:tw-text-white">{fact.value}</dd>
            <dt className="tw-mt-1 tw-truncate tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{fact.label}</dt>
          </div>
        ))}
      </dl>

      {!showHeader && (
        <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1.5 tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
          <WorkerStatusBadge status={summary.status} />
          <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-tabular-nums">
            <LuCalendarDays aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-slate-400" />
            {label('assignedAt')}: {formatAssignedDate(worker.assignedAt, locale) || '—'}
          </span>
        </p>
      )}

      <section className="tw-block">
        <p className={SECTION_TITLE}>{label('roles')}</p>
        {summary.uniqueRoles.length > 0 ? (
          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            {summary.uniqueRoles.map((role) => (
              <span key={role} className="tw-rounded-full tw-bg-violet-500/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-violet-800 dark:tw-text-violet-200">{roleLabel(t, role)}</span>
            ))}
          </div>
        ) : (
          <p className="tw-px-0.5 tw-text-sm tw-text-slate-400 dark:tw-text-slate-500">—</p>
        )}
      </section>

      <section className="tw-block">
        <p className={SECTION_TITLE}>{label('assignments')}</p>
        <ul className="tw-space-y-1.5">
          {centers.map((center) => {
            const leftOn = center.leftEntry ? formatLeftDate(center.leftEntry, locale) : '';
            const tags = [center.workerType, center.blindStatus]
              .filter(Boolean)
              .map((value) => (KNOWN_TAGS.includes(value) ? t(`projectManagement.workerForm.${value}`) : value));
            return (
              <li
                key={center.centerId || 'none'}
                className={`tw-flex tw-items-start tw-gap-2.5 tw-rounded-xl tw-border tw-px-3 tw-py-2.5 ${
                  center.status === 'left'
                    ? 'tw-border-dashed tw-border-slate-200 tw-bg-transparent dark:tw-border-white/10'
                    : 'tw-border-slate-200 tw-bg-white/60 dark:tw-border-white/10 dark:tw-bg-white/[0.03]'
                }`}
              >
                <LuBuilding2 aria-hidden="true" className={`tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 ${center.status === 'left' ? 'tw-text-slate-300 dark:tw-text-slate-600' : 'tw-text-violet-500 dark:tw-text-violet-300'}`} />
                <div className="tw-min-w-0 tw-flex-1">
                  <p className={`tw-break-words tw-text-sm tw-font-semibold ${center.status === 'left' ? 'tw-text-slate-500 dark:tw-text-slate-400' : 'tw-text-slate-800 dark:tw-text-slate-100'}`}>
                    {center.centerName || label('unknownCenter')}
                  </p>
                  <p className="tw-mt-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                    {center.roles.map((role) => roleLabel(t, role)).join(' · ') || '—'}
                    {tags.length > 0 && <span className="tw-text-slate-400 dark:tw-text-slate-500">{` · ${tags.join(' · ')}`}</span>}
                  </p>
                </div>
                <span className="tw-shrink-0">
                  <WorkerStatusBadge
                    status={center.status}
                    label={center.status === 'left' && leftOn ? label('leftOn', { date: leftOn }) : undefined}
                  />
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      {info && (
        <section className="tw-block">
          <p className={SECTION_TITLE}>{label('info')}</p>
          <p className="tw-flex tw-items-start tw-gap-2 tw-rounded-xl tw-bg-slate-900/[0.03] tw-px-3 tw-py-2.5 tw-text-sm tw-text-slate-700 dark:tw-bg-white/[0.04] dark:tw-text-slate-200">
            <LuInfo aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
            <span className="tw-min-w-0 tw-whitespace-pre-line tw-break-words">{info}</span>
          </p>
        </section>
      )}
    </div>
  );
};

WorkerDetailCard.propTypes = {
  worker: PropTypes.shape({
    name: PropTypes.string,
    surname: PropTypes.string,
    assignedAt: PropTypes.any,
    centers: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  summary: PropTypes.object.isRequired,
  showHeader: PropTypes.bool,
};

/** Düzenle / Dosya Yükle / (yalnızca ana admin) Projeden Tamamen Çıkar. */
export const WorkerActions = ({ onEdit, onUpload, onRemove, canRemove, stretch }) => {
  const { t } = useTranslation();
  const label = (key) => t(`projectManagement.workers.actions.${key}`);
  return (
    <>
      <Button variant="primary" size="sm" onClick={onEdit} className={stretch ? 'tw-flex-1' : ''}>
        <LuPencil aria-hidden="true" className="tw-h-4 tw-w-4" />
        {label('edit')}
      </Button>
      <Button size="sm" onClick={onUpload} className={stretch ? 'tw-flex-1' : ''}>
        <LuUpload aria-hidden="true" className="tw-h-4 tw-w-4" />
        {label('upload')}
      </Button>
      {canRemove && (
        <Button variant="danger" size="sm" onClick={onRemove} title={label('removeHint')} className={stretch ? 'tw-w-full' : ''}>
          <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
          {label('remove')}
        </Button>
      )}
    </>
  );
};

WorkerActions.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool,
  stretch: PropTypes.bool,
};

export default WorkerDetailCard;
